import {
	ReactElement,
	RefObject,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import { SettingsPage } from "../SettingsPage";
import {
	BusyIndicator,
	computeContextMenuTitle,
	ContextMenu,
	FloatingActionButton,
	FormattedText,
	Pagination,
	SearchInputForm,
	SearchPlaceholder,
	SelectItem,
	TableView,
	TableViewNoResultConfig,
	TagList,
	TagModel,
	ToolbarAction,
	ToolbarRegularAction,
	useConfirmationDialog,
	useContextMenu
} from "client/Components";
import {
	AlertManager,
	createLocalDataSource,
	MediaQueryKind,
	SelectableItemOptions,
	useAlerts,
	useDeepComparison,
	useFilterHandler,
	useSelection,
	useUnmountedCancellation,
	useWorkspaceMediaQuery
} from "client/Hooks";
import {
	createDraft,
	deleteVersions,
	IntegrationFlowHistoryModel,
	IntegrationFlowKind,
	IntegrationFlowStatus,
	IntegrationFlowVersionModel
} from "client/Api/IntegrationBuilder/integrationBuilderFlows";
import { contains } from "client/Data/searching";
import { Guid } from "client_legacy/ndaw/guid";
import { localize } from "client_legacy/ndaw/culture";
import {
	EMPTY_ARRAY,
	generateTrackingId,
	isNil,
	isNotNil
} from "client/Misc/utils";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import {
	ActionKind,
	buildActionsForTopBar,
	deselectAllTitle
} from "client_legacy/ndaw/actions";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import styles from "./FlowsTabContent.module.less";
import { FilterForm } from "../FilterForm/FilterForm";
import { integrationBuilderRouting } from "./OverViewPage";
import { NavigationCallback } from "client/Navigation";
import { useManageIntegrationPermissionsDialog } from "./ManageIntegrationPermissionsDialog";
import { TagViewModel } from "client/Api/Tags/tags";
import { useTypeSelectionDialog } from "./TypeSelectionDialog";
import { integrationFlowKindToLocalizationKey } from "./common";

type FlowsTabContentProps = {
	appId: number;
	flows: readonly IntegrationFlowHistoryModel[];
	creatableKinds: readonly IntegrationFlowKind[];
	isActive: boolean;
	onActionsStateChange(
		actions: readonly ToolbarAction<FlowActionContext>[],
		context: FlowActionContext
	): void;
	onConfirmUnsavedChanges: () => Promise<boolean>;
	onNavigate: NavigationCallback;
	onReload: () => void;
};

export const FlowsTabContent = (props: FlowsTabContentProps): ReactElement => {
	const {
		appId,
		flows,
		creatableKinds,
		isActive,
		onActionsStateChange,
		onConfirmUnsavedChanges,
		onNavigate,
		onReload
	} = props;

	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [searchTerm, setSearchTerm] = useState<string>("");
	const [currentPageNumber, setCurrentPageNumber] = useState<number>(0);
	const [selectedItems, setSelectedItems] = useState<readonly IntegrationFlowVersionItem[]>(EMPTY_ARRAY);

	const [filter, setFilter] = useState<FilterState>({
		status: null
	});

	const { filteringFor } = useFilterHandler<FilterState>(filter, setFilter);
	const itemsModifiersVersion: number = useDeepComparison([searchTerm, filter]);
	const tableRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);
	const [onContextMenu, contextMenuProps] = useContextMenu(tableRef);
	const alertManager: AlertManager = useAlerts();
	const [confirmationDialog, openConfirmationDialog] = useConfirmationDialog();
	const [typeSelectionDialog, openTypeSelectionDialog] = useTypeSelectionDialog();
	const [permissionsDialog, openPermissionsDialog] = useManageIntegrationPermissionsDialog();
	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const isResponsive: boolean = useWorkspaceMediaQuery(MediaQueryKind.Responsive);
	const isMobile: boolean = useWorkspaceMediaQuery(MediaQueryKind.Mobile);

	const items = useMemo(
		(): readonly IntegrationFlowVersionItem[] => {
			const result: IntegrationFlowVersionItem[] = [];

			for (const flow of flows) {
				for (const version of orderVersions(flow.versions)) {
					result.push({
						...version,
						kind: flow.kind,
						integrationId: flow.id
					});
				}
			}

			return result;
		},
		[flows]
	);

	const integrationWithLiveVersionIds = useMemo(
		(): Set<number> => {
			const result: Set<number> = new Set<number>();

			for (const flow of flows) {
				const hasLiveVersion: boolean = flow
					.versions
					.some(x => x.status === IntegrationFlowStatus.Live);

				if (hasLiveVersion) {
					result.add(flow.id);
				}
			}

			return result;
		},
		[flows]
	);

	const {
		currentItems,
		pagination
	} = useDataSource(
		items,
		{
			pagination: {
				currentPageNumber
			},
			filtering: filter,
			searchTerm
		}
	);

	const {
		multiSelection,
		selectionFor
	} = useSelection(
		currentItems,
		"id",
		{
			isMultiSelect: true,
			selectedItems,
			setSelectedItems
		}
	);

	const createNewHandler = useCallback(
		async (): Promise<void> => {
			if (creatableKinds.length === 0) {
				throw new Error("There is no creatable integration kind");
			}

			const isConfirmed: boolean = await onConfirmUnsavedChanges();

			if (!isConfirmed) {
				return;
			}

			const result: TryResult<IntegrationFlowKind> = await openTypeSelectionDialog({
				creatableKinds
			});

			if (!result.wasSuccessful) {
				return;
			}

			onNavigate(
				integrationBuilderRouting.newFlow({
					routeParams: {
						appId: `${appId}`
					},
					queryParams: {
						flowKind: `${result.result}`
					}
				})
			);
		},
		[
			appId,
			onConfirmUnsavedChanges,
			onNavigate,
			openTypeSelectionDialog,
			creatableKinds
		]
	);

	const deleteHandler = useCallback(
		async (items: readonly IntegrationFlowVersionItem[]): Promise<void> => {
			if (items.length === 0) {
				return;
			}

			const integrationId: number = items[0].integrationId;

			if (hasMultipleFlows(items)) {
				throw new Error("Deleting versions of multiple flows is not supported");
			}

			const result = await openConfirmationDialog({
				title: items.length === 1
					? localize("_AreYouSureYouWantToDeleteThisFlow")
					: localize("_AreYouSureYouWantToDeleteTheseFlows"),
				children: items.some(x => x.status === IntegrationFlowStatus.Live)
					? (
						<span className={styles.error}>
							<FormattedText
								text={localize("_YouAreAboutToDeleteALiveFlowVersionInfo")}
								thingsToWrap={[
									{
										blockId: "Note",
										wrap: (children) => (
											<b>{children}</b>
										)
									}
								]}
							/>
						</span>
					)
					: undefined,
				confirmText: localize("_Delete"),
				trackingId: "delete-flow-versions-confirmation-dialog"
			});

			if (!result.wasSuccessful) {
				return;
			}

			setIsBusy(true);

			try {
				await deleteVersions(
					integrationId,
					items.map(i => i.id),
					globalCancellation
				);

				onReload();
				setSelectedItems(EMPTY_ARRAY);
				setIsBusy(false);
				alertManager.showSuccess(localize("_SuccessfullyDeletedTheItems"));
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				console.error(e);
				alertManager.showError(localize("_GeneralErrorMessage"));
				setIsBusy(false);
			}
		},
		[
			alertManager,
			globalCancellation,
			onReload,
			openConfirmationDialog
		]
	);

	const navigateToVersion = useCallback(
		(integrationId: number, versionId: Guid): void => {
			onNavigate(
				integrationBuilderRouting.editFlow({
					appId: `${appId}`,
					integrationId: `${integrationId}`,
					versionId: `${versionId}`
				})
			);
		},
		[appId, onNavigate]
	);

	const openHandler = useCallback(
		async (item: IntegrationFlowVersionItem): Promise<void> => {
			const isConfirmed: boolean = await onConfirmUnsavedChanges();

			if (!isConfirmed) {
				return;
			}

			navigateToVersion(item.integrationId, item.id);
		},
		[navigateToVersion, onConfirmUnsavedChanges]
	);

	const editHandler = useCallback(
		async (item: IntegrationFlowVersionItem): Promise<void> => {
			const isConfirmed: boolean = await onConfirmUnsavedChanges();

			if (!isConfirmed) {
				return;
			}

			if (
				item.status === IntegrationFlowStatus.Live
					&& !doesDraftExistForFlow(item.integrationId, flows)
			) {
				const result = await openConfirmationDialog({
					title: localize("_CreateDraft"),
					children: localize("_IfYouWantToEditTheFlowADraftWillBeCreatedInfo"),
					confirmText: localize("_CreateDraft"),
					trackingId: "create-draft-confirmation-dialog"
				});

				if (!result.wasSuccessful) {
					return;
				}

				setIsBusy(true);

				try {
					const {
						integrationId,
						versionId
					} = await createDraft(
						item.integrationId,
						item.id,
						globalCancellation
					);

					setIsBusy(false);
					navigateToVersion(integrationId, versionId);
				} catch(e) {
					if (isCancelledError(e)) {
						return;
					}
					console.error(e);
					alertManager.showError(localize("_GeneralErrorMessage"));
					setIsBusy(false);
				}
			} else {
				navigateToVersion(item.integrationId, item.id);
			}
		},
		[
			alertManager,
			flows,
			globalCancellation,
			navigateToVersion,
			onConfirmUnsavedChanges,
			openConfirmationDialog
		]
	);

	const managePermissionsHandler = useCallback(
		async (item: IntegrationFlowVersionItem): Promise<void> => {
			await openPermissionsDialog({
				item: {
					id: item.integrationId,
					kind: item.kind,
					name: item.name
				}
			});
		},
		[openPermissionsDialog]
	);

	const actionHandlers = useMemo(
		(): ActionHandlers => ({
			open: openHandler,
			edit: editHandler,
			deleteItem: deleteHandler,
			removeSelection: (): void => {
				setSelectedItems(EMPTY_ARRAY);
			},
			managePermissions: managePermissionsHandler
		}),
		[
			editHandler,
			deleteHandler,
			openHandler,
			managePermissionsHandler
		]
	);

	const mainAction = useMemo(
		(): ToolbarRegularAction<FlowActionContext> => {
			return getMainAction(createNewHandler);
		},
		[createNewHandler]
	);

	const selectionActions = useMemo(
		(): readonly FlowSelectionAction[] => {
			return getSelectionActions({
				deselectHandler: (): void => setSelectedItems(EMPTY_ARRAY)
			});
		},
		[]
	);

	const contextualActions = useMemo(
		(): readonly FlowSelectionAction[] => {
			return listContextualActions(actionHandlers);
		},
		[actionHandlers]
	);

	const actionContext = useMemo(
		(): FlowActionContext => ({
			selection: selectedItems,
			isMobile,
			integrationWithLiveVersionIds,
			flows
		}),
		[
			isMobile,
			integrationWithLiveVersionIds,
			selectedItems,
			flows
		]
	);

	useEffect(
		(): (() => void ) | void => {
			if (!isActive) {
				return;
			}

			onActionsStateChange(
				buildActionsForTopBar<FlowActionContext, FlowSelectionAction>(
					{
						contextualActions,
						selectionActions,
						mainActions: [mainAction]
					},
					actionContext.selection.length !== 0,
					isResponsive,
					false
				),
				actionContext
			);

			return (): void => {
				onActionsStateChange(
					[],
					{
						selection: [],
						isMobile: false,
						integrationWithLiveVersionIds: new Set(),
						flows: []
					}
				);
			};
		},
		[
			mainAction,
			selectionActions,
			contextualActions,
			isResponsive,
			isActive,
			actionContext,
			onActionsStateChange
		]
	);

	useEffect(
		(): void => {
			hasImplicitDependencyOn(itemsModifiersVersion);
			setCurrentPageNumber(0);
		},
		[itemsModifiersVersion]
	);

	useEffect(
		(): void => {
			hasImplicitDependencyOn(itemsModifiersVersion, currentPageNumber);
			tableRef.current
				?.scrollTo(0, 0);
		},
		[itemsModifiersVersion, currentPageNumber]
	);

	const noResultConfig = useMemo(
		(): TableViewNoResultConfig | undefined => {

			const isEmpty: boolean = !isBusy
				&& currentItems.length === 0
				&& currentPageNumber === 0
				&& isNil(filter.status);

			if (isEmpty) {
				if (searchTerm) {
					return {
						icon: "magnifying-glass",
						title: localize("_NoSearchResults"),
						message: localize("_PleaseTrySearchingWithAnotherTerm")
					};
				}

				return {
					icon: "gear",
					title: localize("_NoFlowsYet"),
					message: localize("_ThereAreNoFlowsYet")
				};
			}

			return undefined;
		},
		[
			currentPageNumber,
			isBusy,
			searchTerm,
			currentItems.length,
			filter.status
		]
	);

	return (
		<>
			{isBusy && <BusyIndicator />}
			<SettingsPage.Section height="stretch">
				<SettingsPage.SearchSection>
					<SearchInputForm
						onChange={setSearchTerm}
						trackingId="search-term"
						icon="filter"
						height="large"
						placeholder={(
							<SearchPlaceholder
								kind="filter"
								properties={[localize("_Name")]}
							/>
						)}
					/>
				</SettingsPage.SearchSection>
				<SettingsPage.Section
					height="stretch"
				>
					<TableView
						multiSelectable={multiSelection}
						trackingId="flows-table"
						items={currentItems}
						columns={[
							{
								title: localize("_Name"),
								trackingId: "name",
								width: "fill"
							},
							{
								title: localize("_Type"),
								trackingId: "type",
								width: "fill"
							},
							{
								title: localize("_Status"),
								trackingId: "status",
								width: "fill",
								filterable: {
									columnFilterableFormFactory: (onClose) => (
										<FilterForm
											filterable={filteringFor("status")}
											onClose={onClose}
											statusFilterOptions={statusFilterOptions}
										/>
									),
									isActive: isNotNil(filter.status)
								}
							},
							{
								title: localize("_Steps"),
								trackingId: "status",
								width: "fill"
							},
							{
								title: localize("_Tags"),
								trackingId: "tags",
								width: "fill"
							}
						]}
						ref={tableRef}
						height="stretch"
						itemKey="id"
						noResultConfig={noResultConfig}
					>
						{item => {
							const selectableOptions: SelectableItemOptions = selectionFor(item);
							const tags: readonly TagModel<number>[] = item
								.tags
								.map(convertTagForList);

							return (
								<TableView.Row
									selectable={selectableOptions}
									trackingId={item.id}
									onContextMenu={onContextMenu}
									onExecute={() => openHandler(item)}
								>
									<TableView.Cell
										tooltip={item.name}
										shouldExecuteOnClick={item.status !== IntegrationFlowStatus.Blocked}
									>
										{hasIndention(item, integrationWithLiveVersionIds) && (
											<span className={styles.indent}/>
										)}
										{item.name}
									</TableView.Cell>
									<TableView.Cell>
										{localize(integrationFlowKindToLocalizationKey[item.kind])}
									</TableView.Cell>
									<TableView.Cell>
										{localize(itemStatusToLocalizationKeyMap[item.status])}
									</TableView.Cell>
									<TableView.Cell>{item.stepsCount}</TableView.Cell>
									<TableView.Cell>
										<TagList
											trackingId={generateTrackingId("flow-tag-list", `|${item.id}`)}
											tags={tags}
											showMoreTagsColor="secondary"
											shouldBubbleTagClick={false}
										/>
									</TableView.Cell>
								</TableView.Row>
							);
						}}
					</TableView>
					<Pagination
						{...pagination}
						onPageNumberChanged={setCurrentPageNumber}
					/>
					{contextMenuProps && (
						<ContextMenu
							{...contextMenuProps}
							actions={contextualActions}
							context={actionContext}
							trackingId={generateTrackingId("flows-overview-context-menu")}
							title={computeContextMenuTitle(
								actionContext.selection,
								(x) => x.name
							)}
						/>
					)}
				</SettingsPage.Section>
			</SettingsPage.Section>
			<div className="preserve-space-for-mobile-plus-button"/>
			{isActive && mainAction.canExecute(actionContext) && (
				<FloatingActionButton
					icon="plus"
					onClick={() => mainAction.execute(actionContext)}
					trackingId="create-flow-fab"
				/>
			)}

			{confirmationDialog}
			{permissionsDialog}
			{typeSelectionDialog}
		</>
	);
};
FlowsTabContent.displayName = "FlowsTabContent";

export const itemStatusToLocalizationKeyMap: {[key in IntegrationFlowStatus]: LocalizationKey} = {
	[IntegrationFlowStatus.Live]: "_Live",
	[IntegrationFlowStatus.Draft]: "_Draft",
	[IntegrationFlowStatus.Blocked]: "_Blocked",
};

type FilterState = {
	status: IntegrationFlowStatus | null;
};

const statusFilterOptions: readonly SelectItem<IntegrationFlowStatus>[] = [
	{
		value: IntegrationFlowStatus.Live,
		text: localize(itemStatusToLocalizationKeyMap[IntegrationFlowStatus.Live])
	},
	{
		value: IntegrationFlowStatus.Draft,
		text: localize(itemStatusToLocalizationKeyMap[IntegrationFlowStatus.Draft])
	},
	{
		value: IntegrationFlowStatus.Blocked,
		text: localize(itemStatusToLocalizationKeyMap[IntegrationFlowStatus.Blocked])
	}
];

type IntegrationFlowVersionItem = IntegrationFlowVersionModel & {
	kind: IntegrationFlowKind;
	integrationId: number;
};

type ActionHandlers = {
	open(item: IntegrationFlowVersionItem): void;
	edit(item: IntegrationFlowVersionItem): void;
	deleteItem(items: readonly IntegrationFlowVersionItem[]): void;
	removeSelection(): void;
	managePermissions(item: IntegrationFlowVersionItem): void;
};

export type FlowActionContext = {
	selection: readonly IntegrationFlowVersionItem[];
	isMobile: boolean;
	integrationWithLiveVersionIds: Set<number>;
	flows: readonly IntegrationFlowHistoryModel[];
};

export type FlowSelectionAction = ToolbarAction<FlowActionContext>;

function listContextualActions(
	handlers: ActionHandlers,
): readonly FlowSelectionAction[] {
	const {
		open,
		edit,
		deleteItem,
		managePermissions
	} = handlers;

	return [
		{
			kind: ActionKind.Regular,
			name: "open",
			title: localize("_Open"),
			canExecute: (c) => c.selection
				.length === 1
					&& c.selection[0]
						.status === IntegrationFlowStatus.Live,
			execute: (c) => open(c.selection[0]),
			icon: "folder-open"
		},
		{
			kind: ActionKind.Regular,
			name: "edit",
			title: localize("_Edit"),
			canExecute: isEditAvailable,
			execute: (c) => edit(c.selection[0]),
			icon: "pen"
		},
		{
			kind: ActionKind.Regular,
			name: "managePermissions",
			title: localize("_ManagePermissions"),
			canExecute: (c) => {
				const {
					selection,
					integrationWithLiveVersionIds
				} = c;

				if (selection.length !== 1) {
					return false;
				}

				const item: IntegrationFlowVersionItem = selection[0];

				return item.status === IntegrationFlowStatus.Live
					|| (item.status === IntegrationFlowStatus.Draft
						&& !integrationWithLiveVersionIds.has(item.integrationId)
					);

			},
			execute: (c) => managePermissions(c.selection[0]),
			icon: "key"
		},
		{
			kind: ActionKind.Regular,
			name: "delete",
			title: localize("_Delete"),
			canExecute: (c) => c.selection
				.length > 0
					&& !hasMultipleFlows(c.selection),
			execute: (c) => deleteItem(c.selection),
			icon: "trash-can"
		}
	];
}

function isEditAvailable(
	context: FlowActionContext
): boolean {
	const {
		selection,
		flows
	} = context;

	if (selection.length !== 1) {
		return false;
	}

	const item: IntegrationFlowVersionItem = selection[0];

	return item.status === IntegrationFlowStatus.Draft
		|| (
			item.status === IntegrationFlowStatus.Live
				&& !doesDraftExistForFlow(
					item.integrationId,
					flows
				)
		);
}

function hasMultipleFlows(
	items: readonly IntegrationFlowVersionItem[]
): boolean {
	if (items.length === 0) {
		return false;
	}

	const integrationId: number = items[0]
		.integrationId;

	return items.some(x => x.integrationId !== integrationId);
};

function orderVersions(
	versions: readonly IntegrationFlowVersionModel[]
): readonly IntegrationFlowVersionModel[] {
	return versions.toSorted(
		(left, right) => getStatusOrder(left.status) - getStatusOrder(right.status)
	);
};

function getStatusOrder(status: IntegrationFlowStatus): number {
	return status === IntegrationFlowStatus.Live
		? 0
		: 1;
};

function hasIndention(
	item: IntegrationFlowVersionItem,
	integrationWithLiveVersionIds: Set<number>
): boolean {
	if (item.status !== IntegrationFlowStatus.Draft) {
		return false;
	}

	return integrationWithLiveVersionIds.has(item.integrationId);
};

function doesDraftExistForFlow(
	integrationId: number,
	flows: readonly IntegrationFlowHistoryModel[]
): boolean {
	const flow: IntegrationFlowHistoryModel | undefined = flows
		.find(x => x.id === integrationId);

	if (!flow) {
		console.warn(`There is no flow item for integration id ${integrationId}`);
		return false;
	}

	return flow.versions
		.some(x => x.status === IntegrationFlowStatus.Draft);
};

function getMainAction(
	createNew: () => void
): ToolbarRegularAction<FlowActionContext> {
	return {
		kind: ActionKind.Regular,
		name: "new",
		title: localize("_New"),
		canExecute: () => true,
		execute: (_) => createNew(),
		icon: "plus",
		isPrimary: true
	};
}

type SelectionActionsHandlers = {
	deselectHandler: () => void;
};

function getSelectionActions(
	handlers: SelectionActionsHandlers
): readonly FlowSelectionAction[] {
	return [
		{
			kind: ActionKind.Regular,
			name: "deselectAll",
			title: (c) => deselectAllTitle(c.selection),
			icon: "circle-xmark",
			canExecute: c => c
				.selection
				.length > 0,
			execute: () => handlers.deselectHandler()
		},
		{
			kind: ActionKind.Divider,
			name: "deselect-all-divider",
			canExecute: (c) => !c.isMobile
				&& c.selection.length > 0
		}
	];
}

const useDataSource = createLocalDataSource<
	IntegrationFlowVersionItem,
	FilterState
>()
	.setConfiguration({
		search: {
			doesMatchItem: searchMatchFunction,
		},
		filtering: { applyFilter },
		hasPagination: true
	})
	.getHook();

function applyFilter(
	items: readonly IntegrationFlowVersionItem[],
	filterState: FilterState
): readonly IntegrationFlowVersionItem[] {
	return items.filter(x =>
		(filterState.status === null || x.status === filterState.status)
	);
}

function searchMatchFunction(
	item: IntegrationFlowVersionItem,
	normalizedNotEmptySearchTerm: Exclude<string, "">
): boolean {
	const {
		name
	} = item;

	return contains(name, normalizedNotEmptySearchTerm);
}

function convertTagForList(tag: TagViewModel): TagModel<number> {
	return {
		value: tag.id,
		text: tag.name,
		color: "secondary"
	};
}
